import { useState, useEffect, useCallback } from 'react';
import { contactService } from '../services/api';

export function useProfiles({ enabled = true } = {}) {
    const [profiles, setContacts] = useState([]);
    const [loading, setLoading] = useState(enabled); // Only load if enabled
    const [error, setError] = useState(null);
    const [pagination, setPagination] = useState({
        page: 1, limit: 50, total: 0, totalPages: 1
    });

    // Fetch profiles with pagination params
    const fetchContacts = useCallback(async (paramsObj = {}) => {
        setLoading(true);
        setError(null);
        try {
            const result = await contactService.getAll(paramsObj);

            setContacts(result.data);
            setPagination(result.pagination);
            return result.data;
        } catch (err) {
            setError(err.message);
            console.error('Failed to fetch profiles:', err);
            return [];
        } finally {
            setLoading(false);
        }
    }, []);

    // Search profiles
    const searchProfiles = useCallback(async (query) => {
        return await fetchContacts({ search: query, page: 1, limit: 50 });
    }, [fetchContacts]);

    useEffect(() => {
        if (enabled) {
            fetchContacts({ page: 1, limit: 50 });
        } else {
            setLoading(false);
        }
    }, [fetchContacts, enabled]);

    const addContact = useCallback(async (contactData) => {
        try {
            const newContact = await contactService.create(contactData);
            setContacts(prev => [newContact, ...prev]);
            setPagination(prev => ({ 
                ...prev, 
                total: prev.total + 1,
                ...(prev.absoluteTotal !== undefined && { absoluteTotal: prev.absoluteTotal + 1 })
            }));
            return newContact;
        } catch (err) {
            setError(err.message);
            throw err;
        }
    }, []);

    const updateContact = useCallback(async (id, contactData) => {
        try {
            const updated = await contactService.update(id, contactData);
            setContacts(prev => prev.map(c => c.id === id ? updated : c));
            return updated;
        } catch (err) {
            setError(err.message);
            throw err;
        }
    }, []);

    const deleteContact = useCallback(async (id) => {
        try {
            await contactService.delete(id);
            setContacts(prev => prev.filter(c => c.id !== id));
            setPagination(prev => ({ 
                ...prev, 
                total: Math.max(0, prev.total - 1),
                ...(prev.absoluteTotal !== undefined && { absoluteTotal: Math.max(0, prev.absoluteTotal - 1) })
            }));
        } catch (err) {
            setError(err.message);
            throw err;
        }
    }, []);

    const deleteAllContacts = useCallback(async (fetchParams = {}) => {
        try {
            const result = await contactService.deleteAll();
            // Re-fetch from server so UI reflects actual state (prequalification-linked profiles are preserved)
            await fetchContacts({ page: 1, limit: fetchParams.limit || 10, search: fetchParams.search || '', source: fetchParams.source || '' });
            return result;
        } catch (err) {
            setError(err.message);
            throw err;
        }
    }, [fetchContacts]);

    const patchProfile = useCallback((id, patch) => {
        setContacts(prev => prev.map(c => c.id === id ? { ...c, ...patch } : c));
    }, []);

    return {
        profiles,
        loading,
        error,
        pagination,
        fetchContacts,
        searchProfiles,
        addContact,
        updateContact,
        patchProfile,
        deleteContact,
        deleteAllContacts
    };
}
